/**
 * @lines98/client/lib/session-revoked-broadcast — кросс-вкладочный мост сигнала
 *   «сессия завершена» (тикет `fe-playtest-round2-2026-06-22.md` §E, AC-E.1..E.8).
 *
 * ЧТО: тонкий DOM-free мост поверх `BroadcastChannel` (fallback — `storage`-event),
 *   который связывает revoked-эпизод одной вкладки со ВСЕМИ остальными вкладками той
 *   же origin. Две стороны:
 *     - ИСХОДЯЩАЯ: когда `emitSessionRevoked` сработал в ЭТОЙ вкладке (api-client
 *       поймал 401 token_not_recognized ИЛИ heartbeat прощупал мёртвый токен) —
 *       мост публикует сообщение в канал, чтобы другие вкладки узнали СРАЗУ.
 *     - ВХОДЯЩАЯ: сообщение из ДРУГОЙ вкладки → мост зовёт `emitSessionRevoked()`
 *       локально → поднимается уже-существующая плашка (`lib/session-revoked-banner`).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-bug мультитап 2026-06-22): single-active-session
 *   уже на сервере (`session_epoch`, мигр. 0019) — вход в тот же аккаунт в другом
 *   браузере отзывает старую сессию. Но прежде revoke детектился ТОЛЬКО когда сама
 *   вкладка шлёт авторизованный запрос и ловит 401 (`api-client`) ИЛИ на возврат
 *   фокуса (`session-heartbeat`). Простаивающая вкладка (не в фокусе, без запросов)
 *   узнавала об отзыве с задержкой. Этот мост закрывает разрыв: ПЕРВАЯ вкладка,
 *   узнавшая о revoke любым путём, мгновенно оповещает остальные той же origin →
 *   каждая немедленно поднимает плашку, не дожидаясь focus/собственного запроса.
 *
 * SCOPE: ТОЛЬКО кросс-вкладочный транспорт сигнала. Плашку (`session-revoked-banner`)
 *   и сам seam (`session-revoked`) НЕ дублирует — переиспользует (модель «ствол →
 *   ветки», CLAUDE.md owner-directive 2026-05-30). Никакого DOM, никакого нового
 *   оверлея. Серверную auth / анти-чит не трогает — лишь транспорт над существующим
 *   клиентским сигналом.
 * NOT IN SCOPE: серверная single-active-session (`session_epoch`); сам детект 401
 *   (это api-client); рендер плашки (это session-revoked-banner).
 *
 * Анти-loop: ВХОДЯЩЕЕ remote-сообщение НЕ ре-броадкастится. Реализовано флагом
 *   `_handlingRemote`: на время вызова `emitSessionRevoked()` из remote-обработчика
 *   флаг поднят → исходящая подписка `onSessionRevoked` видит его и НЕ постит обратно
 *   в канал. Без этого N вкладок устроили бы шторм взаимных публикаций.
 *
 * Де-дуп: серия remote-сообщений (несколько вкладок отвалились разом) не плодит
 *   несколько плашек — гасит `_pending` в самом seam (`session-revoked`): первый
 *   `emitSessionRevoked` ставит флаг, последующие в рамках эпизода — no-op.
 *
 * Гард авторизации: мост активен ТОЛЬКО для авторизованной identity (`isAuthed()`),
 *   как `session-heartbeat`. Гостю re-mint анонима — норма (fresh-browser поведение),
 *   не отзыв → ни исходящий broadcast, ни обработка входящего для гостя не срабатывают.
 *
 * Cross-references:
 *   - lib/session-revoked.ts (seam: emitSessionRevoked / onSessionRevoked / _pending).
 *   - lib/session-revoked-banner.ts (плашка — поднимается на локальный emit; реюз).
 *   - lib/session-heartbeat.ts (гард isAuthed + teardown-симметрия shell-подписок).
 *   - services/identity.ts (isAuthed — авторизован ли игрок).
 *   - app.ts bootShell (installSessionRevokedBroadcast один раз, рядом с
 *     installSessionRevokedBanner / installSessionHeartbeat).
 */

import { isAuthed } from '../services/identity.js';

import { emitSessionRevoked, onSessionRevoked } from './session-revoked.js';

/**
 * Имя кросс-вкладочного канала И ключ localStorage-fallback. Одна строка на оба
 * транспорта — вкладки одной origin слушают один и тот же канал/ключ. Это
 * клиентская транспорт-константа (не CSS-размер, не игровое число) → именованная,
 * не magic-строка (code-structure §4).
 */
const CHANNEL_NAME = 'lines98.session-revoked';

/**
 * Тип сообщения моста. Поле `type` — на случай будущих сообщений в том же канале
 * (forward-compat); сейчас единственное значение.
 */
interface RevokedMessage {
  readonly type: 'session-revoked';
}

/** Singleton-флаг установки: мост ставится один раз на boot (AC-E.8 идемпотентность). */
let _installed = false;

/**
 * Поднят на время обработки ВХОДЯЩЕГО remote-сообщения — чтобы исходящая подписка
 * `onSessionRevoked` не ре-броадкастила то, что только что пришло из канала
 * (анти-loop, AC-E.4). Без него вызванный из remote-обработчика `emitSessionRevoked`
 * дёрнул бы исходящую подписку → новый post → шторм между вкладками.
 */
let _handlingRemote = false;

/**
 * Завести кросс-вкладочный мост revoked-сигнала. Идемпотентно: app-shell зовёт ОДИН
 * раз на boot (рядом с installSessionRevokedBanner / installSessionHeartbeat);
 * повторный вызов в рамках того же boot — no-op (возвращает no-op teardown), чтобы
 * не плодить дублирующие подписки/каналы (AC-E.8).
 *
 * @returns teardown — снимает подписку seam'а + канал/слушатели (симметрия с другими
 *   shell-подписками, AC-E.7). Идемпотентен.
 */
export function installSessionRevokedBroadcast(): () => void {
  if (_installed) return () => {};
  _installed = true;

  const channel = openChannel();

  // --- ВХОДЯЩАЯ ветка: remote-сообщение → локальный emit (плашка СРАЗУ) -------
  // Гард isAuthed: для гостя remote-revoke игнорируем (re-mint — норма, не отзыв).
  // Флаг from-remote гасит ре-броадкаст (анти-loop). _pending в seam гасит дубли
  // серии сообщений (одна плашка на эпизод).
  const handleRemote = (): void => {
    if (!isAuthed()) return;
    _handlingRemote = true;
    try {
      emitSessionRevoked();
    } finally {
      _handlingRemote = false;
    }
  };

  channel.onIncoming(handleRemote);

  // --- ИСХОДЯЩАЯ ветка: локальный revoke → публикация в канал ------------------
  // Подписываемся на seam: когда api-client/heartbeat вызвал emitSessionRevoked в
  // ЭТОЙ вкладке, оповещаем остальные. Гард isAuthed (исходящий) — гость не публикует.
  // Гард _handlingRemote — НЕ постим обратно то, что пришло из канала (анти-loop).
  const unsubscribeSeam = onSessionRevoked(() => {
    if (_handlingRemote) return; // входящее remote не ре-броадкастим
    if (!isAuthed()) return; // гость re-mint — не отзыв, не публикуем
    channel.post({ type: 'session-revoked' });
  });

  return () => {
    unsubscribeSeam();
    channel.close();
    _installed = false;
  };
}

// =============================================================================
// Транспорт: BroadcastChannel с fallback на storage-event
// =============================================================================

/** Унифицированный транспорт-handle: публикация + подписка на входящее + teardown. */
interface ChannelHandle {
  /** Опубликовать сообщение остальным вкладкам той же origin. */
  post(message: RevokedMessage): void;
  /** Подписать обработчик на ВХОДЯЩЕЕ сообщение из другой вкладки. */
  onIncoming(handler: () => void): void;
  /** Снять канал/слушатели. */
  close(): void;
}

/**
 * Открыть транспорт: предпочтительно `BroadcastChannel` (точечная доставка между
 * вкладками); fallback — `localStorage` write + `storage`-event (срабатывает у
 * ДРУГИХ вкладок при записи ключа). Выбор делается один раз на install.
 */
function openChannel(): ChannelHandle {
  const BC = (globalThis as { BroadcastChannel?: typeof BroadcastChannel }).BroadcastChannel;
  if (typeof BC === 'function') {
    return openBroadcastChannel(BC);
  }
  return openStorageFallback();
}

/** BroadcastChannel-ветка транспорта. */
function openBroadcastChannel(BC: typeof BroadcastChannel): ChannelHandle {
  const bc = new BC(CHANNEL_NAME);
  let handler: (() => void) | null = null;

  const onMessage = (ev: MessageEvent): void => {
    // Любое сообщение в нашем канале трактуем как revoked-сигнал (единственный тип).
    const data = ev.data as Partial<RevokedMessage> | undefined;
    if (!data || data.type !== 'session-revoked') return;
    handler?.();
  };
  bc.addEventListener('message', onMessage);

  return {
    post(): void {
      try {
        bc.postMessage({ type: 'session-revoked' });
      } catch {
        // best-effort — мост не должен ронять сетевой/auth-ствол.
      }
    },
    onIncoming(h: () => void): void {
      handler = h;
    },
    close(): void {
      bc.removeEventListener('message', onMessage);
      try {
        bc.close();
      } catch {
        // ignore
      }
    },
  };
}

/**
 * Fallback-ветка через `localStorage` + `storage`-event. Запись ключа в одной
 * вкладке поднимает `storage`-event у ОСТАЛЬНЫХ вкладок (но не у самой пишущей —
 * самозацикливания нет по спецификации события). Значение — меняющийся timestamp,
 * чтобы повторная запись всегда триггерила событие (иначе равное значение его не
 * шлёт).
 */
function openStorageFallback(): ChannelHandle {
  const win = (globalThis as { window?: Window }).window;
  let handler: (() => void) | null = null;

  const onStorage = (ev: StorageEvent): void => {
    if (ev.key !== CHANNEL_NAME) return;
    if (ev.newValue == null) return; // removeItem — не сигнал
    handler?.();
  };
  win?.addEventListener?.('storage', onStorage);

  return {
    post(): void {
      try {
        win?.localStorage?.setItem(CHANNEL_NAME, String(Date.now()));
      } catch {
        // quota / private-mode — best-effort.
      }
    },
    onIncoming(h: () => void): void {
      handler = h;
    },
    close(): void {
      win?.removeEventListener?.('storage', onStorage);
    },
  };
}

/** Test seam: сброс install-флага между кейсами (как session-revoked-banner). */
export function _resetForTests(): void {
  _installed = false;
  _handlingRemote = false;
}
